import React from 'react';

interface AuctionTurnTimerBadgeProps {
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  title?: string;
}

export const AuctionTurnTimerBadge: React.FC<AuctionTurnTimerBadgeProps> = ({
  turnTimerSeconds,
  turnTimerMax = 20,
  className = 'absolute -top-7 left-1/2 -translate-x-1/2 z-30',
  size = 'md',
  title,
}) => {
  if (turnTimerSeconds === undefined || turnTimerSeconds === null) return null;

  const max = turnTimerMax && turnTimerMax > 0 ? turnTimerMax : 20;
  const redThreshold = max <= 5 ? 1 : Math.max(5, Math.round(max * 0.25));
  const warningThreshold = max <= 5 ? Math.min(max - 1, 2) : Math.max(10, Math.round(max * 0.5));

  const ratio = Math.max(0, Math.min(1, turnTimerSeconds / max));
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - ratio);

  const isCritical = turnTimerSeconds <= redThreshold;
  const isWarning = turnTimerSeconds <= warningThreshold && !isCritical;

  const strokeColor = isCritical ? '#f43f5e' : isWarning ? '#f59e0b' : '#10b981';

  const sizeClasses = size === 'sm'
    ? 'w-9 h-9 sm:w-10 sm:h-10 ring-2'
    : size === 'xl'
    ? 'w-20 h-20 sm:w-24 sm:h-24 ring-4'
    : size === 'lg'
    ? 'w-16 h-16 ring-4'
    : 'w-14 h-14 ring-4';

  const textSizeClasses = size === 'sm'
    ? 'text-[11px] sm:text-xs'
    : size === 'xl'
    ? 'text-2xl sm:text-3xl'
    : size === 'lg'
    ? 'text-base sm:text-lg'
    : 'text-sm sm:text-base';

  return (
    <div
      className={`${className} ${sizeClasses} rounded-full flex items-center justify-center select-none ring-[#0a1120] bg-[#0a1120] shadow-[0_4px_16px_rgba(0,0,0,0.9)] transform-gpu [backface-visibility:hidden] [webkit-backface-visibility:hidden]`}
      style={{
        WebkitBackfaceVisibility: 'hidden',
      }}
      title={title || `Turn timer: ${turnTimerSeconds}s remaining`}
    >
      {/* Outer base with single warm metallic gold ring */}
      <div
        className="absolute inset-0 rounded-full bg-[#0a1120] transition-[border-color,box-shadow] duration-300"
        style={{
          border: isCritical ? '1.5px solid #f43f5e' : '1.5px solid #d48b28',
          boxShadow: isCritical
            ? '0 0 10px 1px rgba(244, 63, 94, 0.5)'
            : '0 0 10px 1px rgba(229, 142, 25, 0.35)',
        }}
      />

      {/* SVG Countdown Progress Ring */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible"
        viewBox="0 0 100 100"
      >
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.12)"
          strokeWidth="6"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth="6.5"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          className="transition-[stroke-dashoffset] duration-200 ease-linear"
          filter={
            isCritical
              ? 'drop-shadow(0 0 6px rgba(244,63,94,0.8))'
              : isWarning
              ? 'drop-shadow(0 0 5px rgba(245,158,11,0.6))'
              : 'drop-shadow(0 0 5px rgba(16,185,129,0.6))'
          }
        />
      </svg>

      {/* Numeric seconds in crisp bold white text */}
      <span
        className={`relative z-20 font-sans ${textSizeClasses} font-black tracking-tight leading-none ${
          isCritical ? 'text-rose-400 animate-pulse' : 'text-white'
        }`}
      >
        {turnTimerSeconds}s
      </span>
    </div>
  );
};
